import { z } from 'zod';

/** The three Better Auth calls, and the adapter that makes their errors legible. */

export interface SignInCredentials {
  readonly email: string;
  readonly password: string;
}

export interface SignUpCredentials extends SignInCredentials {
  readonly name: string;
}

/** Better Auth's own minimum. Nothing in the corpus specifies a policy (SG-1). */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * ⚠ Never an `ApiError`, and `isUnauthenticated()` must be false for it — a
 * failed sign-in is HTTP 401, and treating that as the session condition would
 * make `/sign-in` redirect to itself on every wrong password (brief §6.1,
 * `entry-surfaces.md` C4's "silver lining"). `message` is already the mapped,
 * member-facing sentence from `authErrorMessage`; a caller never maps `code`
 * itself.
 */
export class AuthError extends Error {
  override readonly name = 'AuthError';
  /** The Better Auth code (e.g. `INVALID_EMAIL_OR_PASSWORD`), or 'unknown' / 'network'. */
  readonly code: string;
  /** HTTP status, or 0 for a network failure — there is no response to read one from. */
  readonly status: number;
  /**
   * The server's own sentence, when there was one. Carried so a caller WITH a
   * translator can re-resolve `code` through the catalogue and still reach the
   * one arm no catalogue can express (`INVALID_DISPLAY_NAME`). Never rendered
   * on its own by this module.
   */
  readonly serverMessage: string | undefined;

  constructor(code: string, message: string, status: number, serverMessage?: string) {
    super(message);
    this.code = code;
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

/**
 * `/auth/*`'s own error body, parsed leniently rather than against
 * `apiErrorSchema` (C4). Two of this repository's own failure paths —
 * `INVALID_DISPLAY_NAME` from `databaseHooks`, and Fastify's plain-text `Not
 * Found` for a disabled path — never reach `BASE_ERROR_CODES` at all, so a
 * schema that requires either field throws on a response this adapter must
 * still turn into a sentence rather than an uncaught rejection.
 */
const authErrorBodySchema = z.looseObject({
  code: z.string().optional(),
  message: z.string().optional(),
});

/**
 * `code` → catalogue key. `null` is the SERVERTEXT ARM: the sentence came from
 * `apps/server` and no catalogue can translate a response body (FE-80.1
 * signed-out brief §5.5). Exported so the route can resolve the rendered
 * sentence with the interface-language `t` it holds and this module does not —
 * a non-component module must not reach the context (§5.2).
 */
export function authErrorMessageKey(code: string): string | null {
  switch (code) {
    case 'INVALID_EMAIL_OR_PASSWORD':
      return 'signedOut.error.invalidEmailOrPassword';
    case 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL':
      return 'signedOut.error.emailTaken';
    case 'PASSWORD_TOO_SHORT':
      return 'signedOut.error.passwordTooShort';
    case 'PASSWORD_TOO_LONG':
      return 'signedOut.error.passwordTooLong';
    case 'INVALID_EMAIL':
      return 'signedOut.error.invalidEmail';
    case 'MISSING_FIELD':
      return 'signedOut.error.missingField';
    case 'network':
      return 'signedOut.error.network';
    case 'INVALID_DISPLAY_NAME':
      // ⚠ The serverText arm. `apps/server` authored this sentence
      // (`better-auth.ts`'s `databaseHooks`), it arrives in the response body,
      // and a catalogue cannot translate a response body. The caller reads
      // `AuthError.serverMessage` here.
      return null;
    default:
      return 'signedOut.error.generic';
  }
}

/**
 * The Better Auth error table: EIGHT explicit `case` arms plus the default.
 * `authErrorMessageKey` above carries exactly the same eight, and
 * `signed-out.routing.test.ts` asserts that set equality over BOTH function
 * bodies — so this count is held by a test rather than by a reader, which is
 * what the sentence it replaces ("the eight-row table, §3.2 verbatim") was not.
 * Exported so the table has exactly one home and the test can assert every row
 * without re-deriving one.
 *
 * ⚠ `case 'network':` below is a `FE-80.1` addition and a WIDENING of a shipped
 * contract: `authErrorMessage('network', x)` used to fall through to the
 * default and return `Something went wrong. Try again.`. It is unreachable
 * through `callAuth` — the network path throws before any body is parsed — and
 * `auth.test.ts`, this slice's UNEDITED oracle, has no `network` row, so the
 * pin is the only thing that records it. (`auth.test.ts:250` still says "the
 * eight-row table"; correcting it would edit the oracle, so it is reported in
 * the returns instead.)
 *
 * ⚠ This is the LAST-RESORT English `AuthError.message`, for a caller with no
 * translator in hand — the shape `Error` requires. A caller that has one
 * re-resolves the rendered sentence from `code` through `authErrorMessageKey`
 * and the catalogue (FE-80.1 signed-out brief §5.2). The English here and the
 * `signedOut.error.*` catalogue values are held in lockstep, byte for byte, by
 * `signed-out.routing.test.ts`.
 */
export function authErrorMessage(
  code: string,
  serverMessage: string | undefined,
): string {
  switch (code) {
    case 'INVALID_EMAIL_OR_PASSWORD':
      return 'That email and password do not match an account.';
    case 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL':
      return 'An account already exists for that email. Sign in instead.';
    case 'PASSWORD_TOO_SHORT':
      // Byte-identical to the table's wording; kept in sync with the constant
      // below instead of a second hardcoded "8" that could drift from it.
      return `Your password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    case 'PASSWORD_TOO_LONG':
      return 'That password is too long.';
    case 'INVALID_EMAIL':
      return 'That does not look like an email address.';
    case 'MISSING_FIELD':
      return 'Fill in every field.';
    // ⚠ Byte-identical to `callAuth`'s old inline literal, moved here so a
    // caller re-resolving by CODE cannot silently swap it for the generic
    // sentence. No full stop — the source has none.
    case 'network':
      return 'Could not reach the server';
    case 'INVALID_DISPLAY_NAME':
      // This repo's own `databaseHooks` check, not a `BASE_ERROR_CODES` entry —
      // there is no fixed sentence to translate to, so the server's own message
      // (already member-facing: "Your name must be between 1 and 128
      // characters") passes through verbatim.
      return serverMessage ?? 'Something went wrong. Try again.';
    default:
      return 'Something went wrong. Try again.';
  }
}

/**
 * `/auth/*` is mounted at the server's own path, not proxied through
 * `api.ts`'s `BASE` — see brief §3.1 ("the two files are separate agents").
 */
const AUTH_BASE = '/api/auth';

/**
 * The one `fetch` in this file. Deliberately not `api.ts`'s `request()`:
 * that parses failures with `apiErrorSchema`, which does not describe Better
 * Auth's body, and a wrong password would render "Unexpected 401 response"
 * (C4) instead of a sentence a member can act on.
 */
async function callAuth(path: string, body?: Record<string, unknown>): Promise<void> {
  let response: Response;

  try {
    response = await fetch(`${AUTH_BASE}${path}`, {
      method: 'POST',
      // Same-origin only — the cookie is the identity, and `/api/*` is
      // same-origin through the Vite proxy and through Caddy alike (api.ts
      // argues this at length; the same argument holds here).
      credentials: 'same-origin',
      // ⚠ ONLY when there is a body. `signOutSession` sends none, and Fastify
      // answers a body-less request declaring JSON with
      // `FST_ERR_CTP_EMPTY_JSON_BODY`, a 400 — the bug `api.ts`'s own comment
      // describes, reachable here in exactly the same shape.
      ...(body === undefined ? {} : { headers: { 'content-type': 'application/json' } }),
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      // No `Origin` header: it is a forbidden header name, the browser sets
      // it, and script cannot (brief §3.2, `api.ts` point 3).
    });
  } catch {
    throw new AuthError('network', authErrorMessage('network', undefined), 0);
  }

  if (response.ok) return;

  const text = await response.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      // Fastify's plain-text 404 for a disabled path lands here. Not a bug —
      // it is exactly the case the lenient schema below exists for.
      json = null;
    }
  }

  const parsed = authErrorBodySchema.safeParse(json);
  const code = parsed.success ? (parsed.data.code ?? 'unknown') : 'unknown';
  const serverMessage = parsed.success ? parsed.data.message : undefined;

  throw new AuthError(
    code,
    authErrorMessage(code, serverMessage),
    response.status,
    serverMessage,
  );
}

/**
 * `autoSignIn` is on, so a 200 here already set the session cookie — a
 * sign-up **is** a sign-in. Returns `void`: the response is `{ token, user }`
 * in Better Auth's own field names, not `ownProfileSchema`, and nothing
 * downstream may use that shape (brief §5.4). The caller re-fetches `GET
 * /me` itself.
 */
export async function signUpWithEmail(credentials: SignUpCredentials): Promise<void> {
  await callAuth('/sign-up/email', { ...credentials });
}

/** Same `void` reasoning as `signUpWithEmail` — see its docstring. */
export async function signInWithEmail(credentials: SignInCredentials): Promise<void> {
  await callAuth('/sign-in/email', { ...credentials });
}

/** No body — see the content-type guard in `callAuth`. */
export async function signOutSession(): Promise<void> {
  await callAuth('/sign-out');
}
